import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './course-details.css';

type CourseTabId = 'jee' | 'neet';

interface CourseTab {
  id: CourseTabId;
  label: string;
  badge: string;
  heroTitle: string;
  heroSubtitle: string;
  heroImage: string;
  highlights: { icon: string; title: string; description: string }[];
  curriculum: {
    title: string;
    icon: string;
    description: string;
    points: string[];
  }[];
  curriculumNote: string;
  brochureLabel: string;
  brochureUrl?: string;
  brochureSecondaryLabel?: string;
  brochureSecondaryUrl?: string;
  ctaPrimary: string;
  ctaSecondary: string;
  bannerTitle: string;
  bannerSubtitle: string;
  bannerCta: string;
}

const courseTabs: CourseTab[] = [
  {
    id: 'neet',
    label: 'NEET Preparation',
    badge: 'Medical Wing',
    heroTitle: 'NEET UG Coaching',
    heroSubtitle:
      'Rigorous mentoring for medical aspirants emphasizing thorough NCERT mastery, in-depth Physics, Chemistry, and Biology, and repeated full-syllabus tests.',
    heroImage: '/mode/neet_course.png',
    highlights: [
      {
        icon: 'groups',
        title: 'Exam-Oriented Specially Designed Batches',
        description:
          'Properly tailored batches for 11,12 and 12 pass students to aim their seats in Government Medical College(GMC and AIIMS).',
      },
      {
        icon: 'school',
        title: 'Batch Modes',
        description:
          'Interactive classroom, live online streaming, and hybrid choices to keep you consistent with school and NEET timelines.',
      },
      {
        icon: 'healing',
        title: 'Key Focus Areas',
        description:
          'Mastery of NCERT concepts, structured health-science mock testing, and advanced data-driven performance analysis to strategically eliminate learning gaps.',
      },
    ],
    curriculum: [
      {
        title: 'Biology',
        icon: 'eco',
        description: 'In-depth coverage of Botany and Zoology aligned to NCERT & NEET exam pattern.',
        points: ['Diversity in Living World', 'Structural Organisation in Plants and Animals','Plant & Human Physiology', 'Genetics & Evolution','Biology Human welfare','Biotechnology'],
      },
      {
        title: 'Physics',
        icon: 'biotech',
        description: 'Conceptual physics modules with numericals mirroring NEET question phrasing.',
        points: ['Mechanics', 'Thermodynamics', 'Electromagnetism', 'Optics','Modern physics'],
      },
      {
        title: 'Chemistry',
        icon: 'science',
        description: 'NCERT-focused sections for physical, inorganic, and organic chemistry with frequent recall drills.',
        points: ['ORGANIC','INORGANIC' ,'PHYSICAL'],
      },
    ],
    curriculumNote: 'Note: Daily doubt resolution, structured test reviews, and concept clinics refine every topic for NEET. ',
    brochureLabel: 'Download Full NEET Syllabus',
    brochureUrl: 'https://drive.google.com/file/d/1HOaKCe962LKptGhVkx95Em-dI4poamoq/view?usp=sharing',
    ctaPrimary: 'Register for Career Guidance ',
    ctaSecondary: 'View Success Stories',
    bannerTitle: 'Ready for Medical excellence?',
    bannerSubtitle: 'Secure your NEET journey with expert mentors and structured mentorship.',
    bannerCta: 'Register for Career Guidance ',
  },
  {
    id: 'jee',
    label: 'JEE Preparation',
    badge: 'Engineering Wing',
    heroTitle: 'JEE Main & Advanced Coaching',
    heroSubtitle:
      'Focused coaching for engineering aspirants with emphasis on conceptual clarity, rigorous problem-solving, and systematic preparation.',
    heroImage:
      '/mode/jee_course.png',
    highlights: [
      {
        icon: 'school',
        title: 'Exam-Oriented Specially Designed Batches',
        description:
          'Specially designed batches for Class 11 (two-year), Class 12 (one-year), and 12th pass  aiming for top AIRs.',
      },
      {
        icon: 'autorenew',
        title: 'Batch Modes',
        description:
          'Flexible offline classroom, interactive live online, and hybrid formats built around your boards and JEE calendar.',
      },
      {
        icon: 'insights',
        title: 'Key Focus Areas',
        description:
          'Clear concepts, smart test practice, and guidance to stay calm and confident in exams.',
      },
    ],
    curriculum: [
      {
        title: 'Physics',
        icon: 'biotech',
        description: 'Mechanics, Waves, Optics, Electricity, and modern Physics with application emphasis.',
        points: ['Mechanics', 'Thermodynamics', 'Electromagnetism', 'Optics','Modern physics'],
      },
      {
        title: 'Chemistry',
        icon: 'science',
        description: 'Inorganic, Physical, and Organic chemistry structured for quick recall and reasoning.',
        points: ['ORGANIC','INORGANIC' ,'PHYSICAL'],
      },
      {
        title: 'Mathematics',
        icon: 'calculate',
        description: 'Problem sets covering algebra, calculus, geometry, and vector geometry with timed drills.',
        points: ['Algebra','Calculus',' Coordinate Geometry','Trigonometry etc.'],
      },
    ],
    curriculumNote: 'Note: All courses include application-based learning modules and daily doubt-clearing sessions tailored for JEE.',
    brochureLabel: 'Download Full JEE Main Syllabus',
    brochureUrl: 'https://drive.google.com/file/d/14qO6XtMvv4aQGv04EjGp7O73TKQ6nTwM/view?usp=sharing',
    brochureSecondaryLabel: 'Download Full JEE Advanced Syllabus',
    brochureSecondaryUrl: 'https://drive.google.com/file/d/1KYeQ1VtPvZ1mNUGeVPW3gz4ppKNZO5Ji/view?usp=sharing',
    ctaPrimary: 'Register for Career Guidance',
    ctaSecondary: 'View Success Stories',
    bannerTitle: 'Ready to ace your exams?',
    bannerSubtitle: 'Join the league of toppers today. Limited seats available for upcoming batches.',
    bannerCta: 'Register  for Career Guidance',
  },
];

const advantageItems = [
  {
    icon: 'emoji_events',
    title: 'Rank-oriented Material',
    description: 'Curated study plans and notes built by mentors who have trained toppers for over a decade.',
  },
  {
    icon: 'assessment',
    title: 'Regular Mock Tests',
    description: 'Offline tests designed as per the NEET pattern to simulate real exam conditions and help students refine their exam strategy.',
  },
  {
    icon: 'person',
    title: 'Personal Attention',
    description: 'Small batch size ensure that every student receives one-to-one doubt-solving and personalized mentoring.',
  },
  {
    icon: 'connect_without_contact',
    title: 'Performance Tracking',
    description: 'Detailed analytics that highlight weak zones and keep the improvement roadmap visible.',
  },
];

const JEE_MOBILE_IMAGE = 'https://files.catbox.moe/6ic0qz.jpg';
const GUIDANCE_FORM_URL = "https://forms.gle/xdExtHf2Yss4ZaHq9";
const PROGRAM_BROCHURE_URL =
  "https://drive.google.com/file/d/1l5rloH-eS_x0pKcriMMPvX-Mg5DIixDU/view?usp=sharing";

const CourseDetails: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CourseTabId>(courseTabs[0].id);
  const activeCourse = useMemo(
    () => courseTabs.find((tab) => tab.id === activeTab) ?? courseTabs[0],
    [activeTab]
  );
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({});
  const [isMobileView, setIsMobileView] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false)
  );

  useEffect(() => {
    const resetState: Record<string, boolean> = {};
    activeCourse.curriculum.forEach((subject) => {
      resetState[subject.title] = false;
    });
    setOpenPanels(resetState);
  }, [activeCourse.id, activeCourse.curriculum]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobileView(window.innerWidth <= 768);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const heroImage = useMemo(() => {
    if (activeCourse.id === 'jee' && isMobileView) {
      return JEE_MOBILE_IMAGE;
    }
    return activeCourse.heroImage;
  }, [activeCourse.id, activeCourse.heroImage, isMobileView]);

  return (
    <div className="course-details-page">
      <div className="course-details-wrapper">
        <section className="course-tab-selector" aria-label="Course tabs">
          {courseTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`course-tab ${activeTab === tab.id ? 'course-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
            </button>
          ))}
          <Link to="/courses/foundation-olympiad" className="course-tab course-tab--link">
            Foundation / Olympiad
          </Link>
        </section>

        <section
          className={`course-hero-card ${
            activeCourse.id === 'jee' ? 'course-hero-card--jee' : 'course-hero-card--neet'
          }`}
          aria-label="Course hero"
        >
          <div className="hero-content">
            <span className="hero-badge">{activeCourse.badge}</span>
            <h1>{activeCourse.heroTitle}</h1>
            <p>{activeCourse.heroSubtitle}</p>
            {activeCourse.id === 'neet' && (
              <p className="neet-samarpan-text">
                🩺 NEET – Samarpan (समर्पण)
                <br />
                For those who choose healing as a purpose.
                <br />A path built on dedication, discipline, and deep commitment 🙏
              </p>
            )}
            {activeCourse.id === 'jee' && (
              <p className="jee-sunischit-text">
                📐 IIT-JEE – Sunischit (सुनिश्चित)
                <br />
                Because engineering dreams need certainty, structure,
                <br />
                and strong foundations 🔍
              </p>
            )}
            <div className="hero-actions">
              <a
                className="hero-cta hero-cta--primary"
                href={GUIDANCE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {activeCourse.ctaPrimary}
              </a>
              <Link className="hero-cta hero-cta--secondary" to="/our-students">
                {activeCourse.ctaSecondary}
              </Link>
            </div>
          </div>
          <div
            className="hero-media"
            role="img"
            aria-label={`${activeCourse.label} background`}
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="hero-overlay" />
          </div>
        </section>

        <section className="course-highlights">
          <h2>Course Highlights</h2>
          <div className="highlight-grid">
            {activeCourse.highlights.map((highlight) => (
              <article key={highlight.title} className="highlight-card">
                <span className="material-symbols-outlined highlight-icon">{highlight.icon}</span>
                <div>
                  <h3>{highlight.title}</h3>
                  <p>{highlight.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="course-curriculum">
        <div className="section-heading">
            <h2>Curriculum &amp; Syllabus</h2>
            <div className="brochure-links">
              {activeCourse.brochureUrl ? (
                <a
                  className="brochure-link"
                  href={activeCourse.brochureUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {activeCourse.brochureLabel}
                </a>
              ) : (
                <span className="brochure-link">{activeCourse.brochureLabel}</span>
              )}
              {activeCourse.brochureSecondaryLabel && activeCourse.brochureSecondaryUrl && (
                <a
                  className="brochure-link"
                  href={activeCourse.brochureSecondaryUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {activeCourse.brochureSecondaryLabel}
                </a>
              )}
            </div>
          </div>
          <p className="subtext">Comprehensive coverage crafted around board + competitive requirements.</p>
          <div className="curriculum-panels">
            {activeCourse.curriculum.map((subject) => (
              <details
                key={subject.title}
                className="curriculum-panel"
                open={!!openPanels[subject.title]}
              >
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    setOpenPanels((prev) => ({
                      ...prev,
                      [subject.title]: !prev[subject.title],
                    }));
                  }}
                >
                  <span className="material-symbols-outlined">{subject.icon}</span>
                  <span>{subject.title}</span>
                  <span
                    className={`material-symbols-outlined expand-indicator ${
                      openPanels[subject.title] ? 'is-open' : ''
                    }`}
                  >
                    expand_more
                  </span>
                </summary>
                <div className="panel-body">
                  <p>{subject.description}</p>
                  <ul>
                    {subject.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
          <div className="highlight-note">
            <span className="material-symbols-outlined">workspace_premium</span>
            <p>{activeCourse.curriculumNote}</p>
          </div>
        </section>

        <section className="course-advantage">
          <h2>The S-CUBUS Advantage</h2>
          <div className="advantage-grid">
            {advantageItems.map((advantage) => (
              <article key={advantage.title} className="advantage-card">
                <span className="material-symbols-outlined advantage-icon">{advantage.icon}</span>
                <div>
                  <h3>{advantage.title}</h3>
                  <p>{advantage.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="course-cta">
          <div className="cta-content">
            <h2>{activeCourse.bannerTitle}</h2>
            <p>{activeCourse.bannerSubtitle}</p>
          </div>
          <div className="cta-actions">
            <a
              className="hero-cta hero-cta--primary"
              href={GUIDANCE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {activeCourse.bannerCta}
            </a>
            <a
              className="hero-cta hero-cta--secondary"
              href={PROGRAM_BROCHURE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Program Brochure
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CourseDetails;
